/**
 * Small shared controls for the Deals admin screens (F-DEAL-N1..N5): the
 * confirm dialog, day toggles, time range, time-window list, item / category
 * pickers and order-type chips. Sizes follow the rest of the admin
 * (default Input/Button, text-sm, rounded-lg).
 */
import { useMemo, useState, type ReactNode } from 'react'
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown, Plus, Search, Trash2, X } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { useCategories } from '@/hooks/useCategories'
import { useEnabledOrderTypes } from '@/hooks/useEnabledOrderTypes'
import type { Category, Product } from '@/types'
import type { ComboView } from '@/types/deals'
import {
  DAY_LETTER,
  WEEK_ORDER,
  hhmmToMinute,
  isOvernight,
  minuteToHHMM,
  type DraftWindow,
} from './dealsText'

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export const COMBOS_QUERY_KEY = ['admin-combos'] as const
export const AUTO_DISCOUNTS_QUERY_KEY = ['admin-auto-discounts'] as const
export const UPSELL_ADMIN_QUERY_KEY = ['admin-upsells'] as const

/** Every deal (combo) on the menu. */
export function useCombos() {
  return useQuery({
    queryKey: COMBOS_QUERY_KEY,
    queryFn: async () => (await apiClient.listCombos()).data ?? ([] as ComboView[]),
    staleTime: 30_000,
  })
}

/**
 * The whole menu plus which ids are deals, for the pickers. Deals are kept in
 * `all` (for names) and left out of `items` (a deal cannot contain a deal).
 */
export function useDealsCatalog() {
  const productsQuery = useQuery({
    queryKey: ['products', 'deals-admin', 'all'],
    queryFn: () => apiClient.getAllProducts(),
    staleTime: 30_000,
  })
  const combosQuery = useCombos()
  const { data: categories = [] } = useCategories()
  return useMemo(() => {
    const all: Product[] = productsQuery.data ?? []
    const comboIds = new Set((combosQuery.data ?? []).map((c) => c.product_id))
    const byId = new Map(all.map((p) => [String(p.id), p]))
    const catById = new Map(categories.map((c) => [String(c.id), c]))
    return {
      isLoading: productsQuery.isLoading,
      all,
      items: all.filter((p) => !comboIds.has(String(p.id))),
      comboIds,
      categories: categories as Category[],
      productName: (id: string) => byId.get(id)?.name,
      productPrice: (id: string) => byId.get(id)?.price,
      categoryName: (id: string) => catById.get(id)?.name,
    }
  }, [productsQuery.data, productsQuery.isLoading, combosQuery.data, categories])
}

export interface OrderTypeOption {
  id: string
  label: string
}

/**
 * Channels a price rule / deal can target: the enabled order types (built-in
 * and custom ot_* such as Foodpanda) plus "Online orders".
 */
export function useOrderTypeOptions(): { options: OrderTypeOption[]; label: (id: string) => string } {
  const { raw } = useEnabledOrderTypes()
  return useMemo(() => {
    const options: OrderTypeOption[] = raw
      .filter((r) => r.enabled !== false)
      .map((r) => ({ id: String(r.id).toLowerCase(), label: r.label || r.id }))
    options.push({ id: 'online', label: 'Online orders' })
    const map = new Map(options.map((o) => [o.id, o.label]))
    for (const r of raw) map.set(String(r.id).toLowerCase(), r.label || r.id)
    return { options, label: (id: string) => map.get(id.toLowerCase()) ?? id }
  }, [raw])
}

// ---------------------------------------------------------------------------
// Confirm dialog (Radix AlertDialog, styled like the app's Dialog)
// ---------------------------------------------------------------------------

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  busy,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description: ReactNode
  confirmLabel: string
  busy?: boolean
  onConfirm: () => void
}) {
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <AlertDialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-2rem)] max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg sm:rounded-lg">
          <div className="space-y-1.5">
            <AlertDialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
              {title}
            </AlertDialogPrimitive.Title>
            <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
              {description}
            </AlertDialogPrimitive.Description>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <AlertDialogPrimitive.Cancel asChild>
              <Button type="button" variant="outline" disabled={busy}>
                Cancel
              </Button>
            </AlertDialogPrimitive.Cancel>
            <Button type="button" variant="destructive" disabled={busy} onClick={onConfirm}>
              {busy ? 'Working…' : confirmLabel}
            </Button>
          </div>
        </AlertDialogPrimitive.Content>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  )
}

// ---------------------------------------------------------------------------
// Segmented control (Reports-style)
// ---------------------------------------------------------------------------

export function Segmented<T extends string>({
  value,
  onChange,
  items,
  ariaLabel,
}: {
  value: T
  onChange: (v: T) => void
  items: Array<{ id: T; label: string; icon?: ReactNode }>
  ariaLabel: string
}) {
  return (
    <div className="flex flex-wrap gap-1 rounded-lg bg-muted/60 p-1" role="tablist" aria-label={ariaLabel}>
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={value === t.id}
          onClick={() => onChange(t.id)}
          className={cn(
            'flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors',
            value === t.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {t.icon}
          {t.label}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Days and times
// ---------------------------------------------------------------------------

/** Mon–Sun toggles. [] = every day. */
export function DayToggles({ value, onChange }: { value: number[]; onChange: (days: number[]) => void }) {
  const set = new Set(value)
  const everyDay = value.length === 0 || value.length === 7
  const toggle = (d: number) => {
    const next = new Set(everyDay ? [] : value)
    if (next.has(d)) next.delete(d)
    else next.add(d)
    const arr = [...next].sort((a, b) => a - b)
    onChange(arr.length === 7 ? [] : arr)
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {WEEK_ORDER.map((d) => {
        const on = !everyDay && set.has(d)
        return (
          <button
            key={d}
            type="button"
            aria-pressed={on}
            onClick={() => toggle(d)}
            className={cn(
              'h-9 min-w-[3rem] rounded-md border px-2 text-sm font-medium transition-colors',
              on ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
            )}
          >
            {DAY_LETTER[d]}
          </button>
        )
      })}
      <span className="ml-1 text-xs text-muted-foreground">{everyDay ? 'Every day' : ''}</span>
    </div>
  )
}

/** Start/end time inputs; overnight (22:00 – 02:00) is allowed and labelled. */
export function TimeRangeFields({
  start,
  end,
  onChange,
  idPrefix,
}: {
  start: number | null
  end: number | null
  onChange: (start: number | null, end: number | null) => void
  idPrefix: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Input
        id={`${idPrefix}-start`}
        type="time"
        aria-label="Start time"
        className="w-36"
        value={minuteToHHMM(start)}
        onChange={(e) => onChange(hhmmToMinute(e.target.value), end)}
      />
      <span className="text-sm text-muted-foreground">to</span>
      <Input
        id={`${idPrefix}-end`}
        type="time"
        aria-label="End time"
        className="w-36"
        value={minuteToHHMM(end)}
        onChange={(e) => onChange(start, hhmmToMinute(e.target.value))}
      />
      {isOvernight(start, end) && <span className="text-xs text-muted-foreground">ends next day</span>}
      {start != null && start === end && <span className="text-xs text-destructive">Start and end cannot be the same</span>}
    </div>
  )
}

/** "Available only at these times" — a list of day + time windows. */
export function WindowListEditor({
  value,
  onChange,
  emptyText,
}: {
  value: DraftWindow[]
  onChange: (next: DraftWindow[]) => void
  emptyText: string
}) {
  const update = (i: number, patch: Partial<DraftWindow>) =>
    onChange(value.map((w, j) => (j === i ? { ...w, ...patch } : w)))
  return (
    <div className="space-y-3">
      {value.length === 0 && <p className="text-sm text-muted-foreground">{emptyText}</p>}
      {value.map((w, i) => (
        <div key={i} className="space-y-2 rounded-lg border p-3">
          <div className="flex items-start justify-between gap-2">
            <DayToggles value={w.days} onChange={(days) => update(i, { days })} />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Remove time window"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
          <TimeRangeFields
            idPrefix={`win-${i}`}
            start={w.start_minute}
            end={w.end_minute}
            onChange={(s, e) => update(i, { start_minute: s, end_minute: e })}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => onChange([...value, { days: [], start_minute: 12 * 60, end_minute: 16 * 60 }])}
      >
        <Plus className="h-4 w-4" />
        Add time window
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Order types
// ---------------------------------------------------------------------------

/** Multi-select chips. [] = all order types. */
export function OrderTypeChips({
  options,
  value,
  onChange,
  allLabel = 'All order types',
}: {
  options: OrderTypeOption[]
  value: string[]
  onChange: (next: string[]) => void
  allLabel?: string
}) {
  const set = new Set(value.map((v) => v.toLowerCase()))
  const toggle = (id: string) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange([...next])
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        aria-pressed={value.length === 0}
        onClick={() => onChange([])}
        className={cn(
          'h-9 rounded-md border px-3 text-sm font-medium transition-colors',
          value.length === 0 ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
        )}
      >
        {allLabel}
      </button>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          aria-pressed={set.has(o.id)}
          onClick={() => toggle(o.id)}
          className={cn(
            'h-9 rounded-md border px-3 text-sm font-medium transition-colors',
            set.has(o.id) ? 'border-primary bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pickers. Inline (not portaled) so they scroll correctly inside a Dialog.
// ---------------------------------------------------------------------------

interface PickRow {
  kind: 'product' | 'category'
  id: string
  name: string
  hint?: string
}

function PickList({
  rows,
  onPick,
  onClose,
  placeholder,
}: {
  rows: PickRow[]
  onPick: (row: PickRow) => void
  onClose: () => void
  placeholder: string
}) {
  const [q, setQ] = useState('')
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const list = needle ? rows.filter((r) => r.name.toLowerCase().includes(needle)) : rows
    return list.slice(0, 200)
  }, [rows, q])
  return (
    <div className="mt-1 rounded-lg border bg-popover p-2 shadow-sm">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus
          value={q}
          placeholder={placeholder}
          className="pl-9"
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') onClose()
            if (e.key === 'Enter') {
              e.preventDefault()
              if (filtered[0]) onPick(filtered[0])
            }
          }}
        />
      </div>
      <div className="mt-2 max-h-60 overflow-y-auto">
        {filtered.length === 0 ? (
          <p className="px-2 py-3 text-sm text-muted-foreground">Nothing matches.</p>
        ) : (
          filtered.map((r) => (
            <button
              key={`${r.kind}:${r.id}`}
              type="button"
              onClick={() => onPick(r)}
              className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-2 text-left text-sm hover:bg-muted"
            >
              <span className="truncate">
                {r.kind === 'category' && <span className="mr-1.5 text-xs text-muted-foreground">Category</span>}
                {r.name}
              </span>
              {r.hint && <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{r.hint}</span>}
            </button>
          ))
        )}
      </div>
      <div className="mt-1 flex justify-end">
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  )
}

/** Pick ONE item. Shows its name on the trigger. */
export function ProductPicker({
  products,
  value,
  onChange,
  money,
  placeholder = 'Choose an item',
  exclude,
}: {
  products: Product[]
  value: string
  onChange: (id: string) => void
  money: (v: number) => string
  placeholder?: string
  exclude?: Set<string>
}) {
  const [open, setOpen] = useState(false)
  const selected = products.find((p) => String(p.id) === value)
  const rows: PickRow[] = useMemo(
    () =>
      products
        .filter((p) => !exclude?.has(String(p.id)) || String(p.id) === value)
        .map((p) => ({ kind: 'product' as const, id: String(p.id), name: p.name, hint: money(p.price) })),
    [products, exclude, value, money],
  )
  return (
    <div className="min-w-0 flex-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-left text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
          {selected ? selected.name : value ? 'Removed item' : placeholder}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <PickList
          rows={rows}
          placeholder="Search items"
          onClose={() => setOpen(false)}
          onPick={(r) => {
            onChange(r.id)
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

/** Pick several items and/or categories, shown as removable chips. */
export function TargetPicker({
  products,
  categories,
  productIds,
  categoryIds,
  onChange,
  money,
  allowCategories = true,
  addLabel = 'Add items or categories',
  max,
}: {
  products: Product[]
  categories: Category[]
  productIds: string[]
  categoryIds: string[]
  onChange: (productIds: string[], categoryIds: string[]) => void
  money: (v: number) => string
  allowCategories?: boolean
  addLabel?: string
  max?: number
}) {
  const [open, setOpen] = useState(false)
  const pSet = new Set(productIds)
  const cSet = new Set(categoryIds)
  const rows: PickRow[] = useMemo(() => {
    const out: PickRow[] = []
    if (allowCategories) {
      for (const c of categories) if (!cSet.has(String(c.id))) out.push({ kind: 'category', id: String(c.id), name: c.name })
    }
    for (const p of products) if (!pSet.has(String(p.id))) out.push({ kind: 'product', id: String(p.id), name: p.name, hint: money(p.price) })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, categories, productIds, categoryIds, allowCategories, money])
  const pName = (id: string) => products.find((p) => String(p.id) === id)?.name ?? 'Removed item'
  const cName = (id: string) => categories.find((c) => String(c.id) === id)?.name ?? 'Removed category'
  const full = max != null && productIds.length + categoryIds.length >= max
  return (
    <div className="space-y-2">
      {(productIds.length > 0 || categoryIds.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {categoryIds.map((id) => (
            <span key={`c-${id}`} className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-1 text-sm">
              <span className="text-xs text-muted-foreground">Category</span> {cName(id)}
              <button
                type="button"
                aria-label={`Remove ${cName(id)}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onChange(productIds, categoryIds.filter((x) => x !== id))}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          {productIds.map((id) => (
            <span key={`p-${id}`} className="inline-flex items-center gap-1 rounded-md border bg-muted/40 px-2 py-1 text-sm">
              {pName(id)}
              <button
                type="button"
                aria-label={`Remove ${pName(id)}`}
                className="text-muted-foreground hover:text-foreground"
                onClick={() => onChange(productIds.filter((x) => x !== id), categoryIds)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
      {!full && (
        <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setOpen((v) => !v)}>
          <Plus className="h-4 w-4" />
          {addLabel}
        </Button>
      )}
      {open && !full && (
        <PickList
          rows={rows}
          placeholder={allowCategories ? 'Search items and categories' : 'Search items'}
          onClose={() => setOpen(false)}
          onPick={(r) => {
            if (r.kind === 'category') onChange(productIds, [...categoryIds, r.id])
            else onChange([...productIds, r.id], categoryIds)
            if (max != null && productIds.length + categoryIds.length + 1 >= max) setOpen(false)
          }}
        />
      )}
    </div>
  )
}

/** Label + hint wrapper used across the editors. */
export function Field({ label, hint, children, htmlFor }: { label: string; hint?: ReactNode; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}
